import { getJSONByUrl } from 'widgets/toolbox/ajax';

interface IPaymentMethodsResponse {
    [renderedOptions: string]: Record<string, string>;
}

const isPaymentMethodsResponse = (value: Record<string, unknown>): value is IPaymentMethodsResponse => {
    return Object.hasOwnProperty.call(value, 'renderedOptions');
};

/**
 * @param PaymentAccordion Base widget for extending
 * @returns Payment Accordion class
 */
function PaymentAccordionCashOnDeliveryClassCreator(PaymentAccordion: import('widgets/checkout/PaymentAccordion').TPaymentAccordion) {
    /**
     * @category widgets
     * @subcategory checkout
     * @class PaymentAccordionCashOnDelivery
     * @augments PaymentAccordion
     * @classdesc Represents PaymentAccordion components that implements logic to validate\submit payment information
     * It has next features:
     * 1. Validates current payment accordion item
     * 2. Handles current payment tab submit by processing current tab data
     *
     * @example <caption>Example of PaymentAccordion widget usage</caption>
     *    <div
     *        data-id="payment"
     *        data-widget="paymentAccordion"
     *        data-forward-to-parent="hidePaymentError:submitCurrentStep:showPaymentError:toggleNextPaymentButton:toggleBillingForm"
     *        data-allow-toggle="false"
     *        data-allow-multiple="false"
     *        data-error-message="${Resource.msg('error.payment.not.selected','checkout',null)}"
     *        data-single-panel="${pdict.order.billing.payment.applicablePaymentMethods.length === 1}"
     *        data-field-name="${pdict.forms.billingForm.paymentMethod.htmlName}"
     *        data-payment-options-url="${URLUtils.url('CheckoutServices-GetPaymentOptions')}"
     *        ${pdict.hidePaymentOptions ? 'hidden' : ''}
     *        data-disabled="${Boolean(pdict.hidePaymentOptions)}"
     *        class="b-payment_accordion ${pdict.order.billing.payment.isZeroTotal ? 'm-zero_payment' : ''}"
     *    >
     *        <div data-ref="container">
     *            <isloop items="${pdict.order.billing.payment.applicablePaymentMethods}" var="paymentOption">
     *                <isinclude template="${require('util/pref').get('checkout.payment.body.template.' + paymentOption.ID, 'checkout/billing/paymentOptions/paymentBody_Default')}"/>
     *            </isloop>
     *        </div>
     *    </div>
     *
     * @property {string} data-field-name - payment field name
     */
    class PaymentAccordionCashOnDelivery extends PaymentAccordion {
        /**
         * @description Returns Widget configuration object
         * @returns result
         */
        prefs() {
            return {
                ...super.prefs()
            };
        }

        /**
         * @description Get payment methods content and render them.
         * @param methodsToAdd - Methods ids that should be added
         */
        addPaymentMethods(methodsToAdd: Array<string> = []) {
            return getJSONByUrl(this.prefs().paymentOptionsUrl, {
                methods: methodsToAdd.join(',')
            }).then(response => {
                if (isPaymentMethodsResponse(response)) {
                    Object.entries(response.renderedOptions).forEach(([, methodContent]) => {
                        this.ref('container').append(methodContent);
                    });
                }
            });
        }
    }

    return PaymentAccordionCashOnDelivery;
}

export type TPaymentAccordionCashOnDelivery = ReturnType<typeof PaymentAccordionCashOnDeliveryClassCreator>;

export type TPaymentAccordionCashOnDeliveryInstance = InstanceType<TPaymentAccordionCashOnDelivery>;

export default PaymentAccordionCashOnDeliveryClassCreator;
